import { useDispatch, useSelector } from 'react-redux';

import { useGetDetailsQuery } from '@epi-api/user';
import { routerPaths } from '@epi-constants/routerPaths';

import { getIsAuthenticated, getUserRoles } from '@epi-selectors/login';
import { SystemRoles } from '@epi-constants/roles';
import { getOnboardingActive } from '@epi-store/onboarding/onboarding.selectors';
import { onboardingActions } from '@epi-store/onboarding/onboarding.slice';
import { FundsLoadingTypes } from '@epi-constants/benefitTypes';
import { goToPage } from '@epi-actions/navigation';
import { skipToken } from '@reduxjs/toolkit/query';
import { getVoucherTypeKey, VoucherTypes } from '@epi-constants/voucherTypes';

interface StepInterface {
  stepNumber: number;
  path: string;
  isAllowed: boolean;
  isLastAllowedPageOfStep?: boolean;
}

export const useOnboardingWizard = () => {
  const roles: string[] = useSelector(getUserRoles);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const keepOnboardingActive = useSelector(getOnboardingActive);
  const isSignatory = roles.includes(SystemRoles.Signatory);
  const dispatch = useDispatch();

  const lunchType = getVoucherTypeKey(VoucherTypes.Lunch);
  const recreationalType = getVoucherTypeKey(VoucherTypes.Recreational);

  const {
    isLoading,
    isSuccess,
    data: flags,
    refetch: refetchFlags
  } = useGetDetailsQuery(isAuthenticated ? undefined : skipToken);

  const voucherOrderPaths = [
    routerPaths.orderDetails,
    routerPaths.orderHistory,
    routerPaths.voucherOrderLunch,
    routerPaths.voucherOrderRecreational,
    ...routerPaths.voucherOrder[lunchType],
    ...routerPaths.voucherOrder[recreationalType],
    routerPaths.voucherOrderEditDelivery,
    routerPaths.voucherOrderEditCompany,
    routerPaths.voucherReorder,
    routerPaths.voucherReorderEditAmount,
    routerPaths.voucherReorderEditDelivery,
    routerPaths.voucherReorderEditCompany,
    routerPaths.voucherOrderPayment
  ];

  const allowedPaths = [
    routerPaths.profile,
    routerPaths.companyInformation,
    routerPaths.help,
    ...voucherOrderPaths
  ];

  const step2: StepInterface[] = [
    {
      stepNumber: 2,
      path: routerPaths.benefitGroups,
      isAllowed: true,
      isLastAllowedPageOfStep: true
    }
  ];

  const steps: StepInterface[] = [
    { stepNumber: 1, path: routerPaths.home, isAllowed: true },
    ...step2,
    {
      stepNumber: 3,
      path: routerPaths.beneficiaries,
      isAllowed: !!flags?.isBenefitsSet,
      isLastAllowedPageOfStep: true
    },

    {
      stepNumber: 4,
      path: routerPaths.loadBenefit,
      isAllowed: !!(flags?.isBenefitsSet && flags?.isEmployeesSet),
      isLastAllowedPageOfStep: true
    },
    {
      stepNumber: 4,
      path: routerPaths.loadCompanyAccount,
      isAllowed: !!(flags?.isBenefitsSet && flags?.isEmployeesSet)
    },
    {
      stepNumber: 4,
      path: routerPaths.requestedLoads,
      isAllowed: !!(flags?.isBenefitsSet && flags?.isEmployeesSet)
    },
    {
      stepNumber: 4,
      path: routerPaths.loadCompanyAccountStatus,
      isAllowed: !!(flags?.isBenefitsSet && flags?.isEmployeesSet)
    },

    {
      stepNumber: 5,
      path: routerPaths.onboardingFinished,
      isAllowed: !!(flags?.isBenefitsSet && flags?.isEmployeesSet)
    }
  ];

  const isOnBoardingActive =
    isAuthenticated &&
    isSignatory &&
    flags &&
    (!(
      flags.isBenefitsSet &&
      flags.isEmployeesSet &&
      (flags.isMoneyTransfered || flags.isLoadInitiated)
    ) ||
      keepOnboardingActive);

  const getRelativePath = (path: string) => path.replace(/^(\/en)/, '');

  const checkIfPathIsAllowed = (path: string) => {
    return (
      !isOnBoardingActive ||
      steps.find(step => step.path === getRelativePath(path))?.isAllowed ||
      allowedPaths.includes(getRelativePath(path))
    );
  };

  const getAllowedPage = (stepsArray: StepInterface[]) => {
    return (
      stepsArray
        .slice()
        .reverse()
        .find(step => step.isAllowed && step.isLastAllowedPageOfStep) ??
      steps[0]
    );
  };

  const lastAllowedStep = getAllowedPage(steps);

  const handleGoOnboardingPayment = () => {
    dispatch(onboardingActions.setOnboardingInProgress(true));
    dispatch(onboardingActions.setIsSecondStepDisplayed(false));
    if (
      flags?.lunchFundsLoadingType === FundsLoadingTypes.TopUpRequest &&
      !flags?.areThereOtherThanLunchBenefits
    ) {
      return dispatch(goToPage(routerPaths.loadCompanyAccount));
    }

    if (flags?.lunchFundsLoadingType === FundsLoadingTypes.DirectLoad) {
      dispatch(onboardingActions.setLoadBenefitPageType('lounari'));
    }
    if (flags?.lunchFundsLoadingType === FundsLoadingTypes.TopUpRequest) {
      dispatch(onboardingActions.setLoadBenefitPageType('others'));
    }
    return dispatch(goToPage(routerPaths.loadBenefit));
  };

  return {
    isOnBoardingActive,
    isLoading,
    isSuccess,
    checkIfPathIsAllowed,
    lastAllowedStep,
    flags,
    refetchFlags,
    handleGoOnboardingPayment
  };
};
